import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { Mail, Loader2, Plus, X, AlertCircle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BackButton, SectionHeading } from '@tendbot/ui'
import { SkeletonCard } from '@tendbot/ui'
import { defaultStagger } from '@tendbot/ui'
import {
  AssistantExpanded,
  OwnEmailsExpanded,
  BlockedSendersExpanded,
  getUserProfile,
  getOwnEmails,
  getBlockedSenders,
  useDefaults,
} from '../settings'
import type { UserData } from '../settings'
import { usePlugins, usePluginConnections } from '../plugins'
import type { EmailDefaultOption } from '../../shared/api/defaults'

interface Props {
  onBack: () => void
  onNavigate?: (screen: string) => void
}

interface LoadedData {
  user: UserData
  ownEmails: string[]
  blockedSenders: string[]
  loginEmail: string
}

export default function EmailScreen({ onBack, onNavigate }: Props) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const { container, item } = defaultStagger

  const [data, setData] = useState<LoadedData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [user, ownEmails, blockedSenders] = await Promise.all([
        getUserProfile(),
        getOwnEmails().catch(() => [] as string[]),
        getBlockedSenders().catch(() => [] as string[]),
      ])
      if (!user) throw new Error(t('profile.failedToLoad'))
      setData({
        user,
        ownEmails,
        blockedSenders,
        loginEmail: user.email ?? '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4">
        <div className="max-w-2xl mx-auto">
          <BackButton onClick={onBack} className="mb-4" />
          <SkeletonCard className="h-32 mb-4" />
          <SkeletonCard className="h-24 mb-4" />
          <SkeletonCard className="h-24" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4">
        <div className="max-w-2xl mx-auto">
          <BackButton onClick={onBack} className="mb-4" />
          <div className="glass-card rounded-xl p-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center mx-auto">
              <AlertCircle size={24} className="text-rose" />
            </div>
            <p className="text-pearl/70 text-sm">{error}</p>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
            >
              <RefreshCw size={14} />
              {tc('actions.retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
        <BackButton onClick={onBack} className="mb-2" />

        {/* Header */}
        <motion.div variants={item} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
              <Mail size={20} className="text-gold" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-ivory font-semibold">{t('email.title')}</h1>
              <p className="text-pearl/60 text-xs">{t('email.subtitle')}</p>
            </div>
          </div>
        </motion.div>

        {/* Assistant inbox (@tend.bot) */}
        <motion.div variants={item} className="mb-6">
          <SectionHeading>{t('email.assistantInbox')}</SectionHeading>
          <div className="glass-card rounded-xl p-4">
            <AssistantExpanded
              initialName={data.user.assistant?.name ?? ''}
              initialEmail={data.user.assistant?.email ?? null}
            />
          </div>
        </motion.div>

        {/* Connected email accounts */}
        <motion.div variants={item} className="mb-6">
          <SectionHeading>{t('email.connectedAccounts')}</SectionHeading>
          <ConnectedEmailAccounts onNavigate={onNavigate} />
        </motion.div>

        {/* Your email addresses */}
        <motion.div variants={item} className="mb-6">
          <SectionHeading>{t('email.ownAddresses')}</SectionHeading>
          <div className="glass-card rounded-xl p-4">
            <OwnEmailsExpanded
              content={{
                type: 'own-emails',
                emails: data.ownEmails,
                loginEmail: data.loginEmail,
              }}
            />
          </div>
        </motion.div>

        {/* Blocked senders */}
        <motion.div variants={item} className="mb-8">
          <SectionHeading>{t('email.blockedSenders')}</SectionHeading>
          <div className="glass-card rounded-xl p-4">
            <BlockedSendersExpanded
              content={{
                type: 'blocked-senders',
                entries: data.blockedSenders,
              }}
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

// ─── Connected email accounts card ─────────────────────────────

interface ConnectedEmailAccountsProps {
  onNavigate?: (screen: string) => void
}

function ConnectedEmailAccounts({ onNavigate }: ConnectedEmailAccountsProps) {
  const { t } = useTranslation('settings')
  const { catalog, installed, installPlugin, refresh } = usePlugins()
  const {
    connections,
    connectingPlugin,
    handleConnectGmail,
    handleConnect,
    handleDisconnect,
  } = usePluginConnections({ catalog, installed, installPlugin, refresh })
  const { data: defaults, saving: savingDefault, error: defaultError, setDefaultEmail } = useDefaults()

  const emailAccounts = useMemo(() => {
    return connections
      .filter(c => c.status === 'active')
      .filter(c => {
        if (c.provider === 'microsoft') return c.scopes?.some(s => s.includes('Mail.Read') || s.includes('Mail.Send'))
        if (c.provider === 'google') return c.scopes?.some(s => s.includes('gmail'))
        return false
      })
  }, [connections])

  const [removingId, setRemovingId] = useState<string | null>(null)
  const handleRemove = async (connectionId: string) => {
    setRemovingId(connectionId)
    try { await handleDisconnect(connectionId) } finally { setRemovingId(null) }
  }

  const hasGoogleWorkspace = catalog.some(p => p.id === 'google-workspace')
  const hasMicrosoft = catalog.some(p => p.id === 'microsoft-365')

  return (
    <div className="glass-card rounded-xl p-4 space-y-3">
      {/* Default email account picker */}
      <DefaultEmailPicker
        options={defaults?.emailOptions ?? []}
        value={defaults?.defaultEmailConnectionId ?? null}
        saving={savingDefault}
        error={defaultError}
        onChange={setDefaultEmail}
      />

      {/* Connected list */}
      {emailAccounts.length === 0 && (
        <p className="text-xs text-pearl/55">{t('email.noConnectedAccounts')}</p>
      )}
      {emailAccounts.map(conn => {
        const isRemoving = removingId === conn.connectionId
        return (
          <div
            key={conn.connectionId}
            className={`flex items-center justify-between py-1.5 ${isRemoving ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <ProviderIcon provider={conn.provider} />
              <div className="min-w-0">
                <p className="text-sm text-ivory truncate">{conn.displayName || t('email.connected')}</p>
                <p className="text-xs text-pearl/50">{providerLabel(conn.provider, t)}</p>
              </div>
            </div>
            {isRemoving ? (
              <Loader2 size={14} className="text-pearl/55 animate-spin" />
            ) : (
              <button
                onClick={() => handleRemove(conn.connectionId)}
                disabled={!!removingId}
                className="p-1 rounded hover:bg-rose/10 transition-colors disabled:opacity-50"
                aria-label={t('email.disconnect')}
              >
                <X size={14} className="text-pearl/60 hover:text-rose" />
              </button>
            )}
          </div>
        )
      })}

      {/* Add buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        {hasGoogleWorkspace && (
          <button
            onClick={handleConnectGmail}
            disabled={connectingPlugin === 'gmail'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors disabled:opacity-50"
          >
            {connectingPlugin === 'gmail' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {t('email.addGmail')}
          </button>
        )}
        {hasMicrosoft && (
          <button
            onClick={() => handleConnect('microsoft-365')}
            disabled={connectingPlugin === 'microsoft-365'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors disabled:opacity-50"
          >
            {connectingPlugin === 'microsoft-365' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {t('email.addOutlook')}
          </button>
        )}
        <button
          onClick={() => onNavigate?.('plugins')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-pearl/70 hover:text-ivory hover:bg-slate-mid/30 transition-colors"
        >
          {t('email.moreOptions')}
        </button>
      </div>

      <p className="text-xs text-pearl/50 pt-1 leading-relaxed">
        {t('email.connectedAccountsHint')}
      </p>
    </div>
  )
}

interface DefaultEmailPickerProps {
  options: EmailDefaultOption[]
  value: string | null
  saving: boolean
  error: string | null
  onChange: (connectionId: string | null) => Promise<void>
}

function DefaultEmailPicker({ options, value, saving, error, onChange }: DefaultEmailPickerProps) {
  const { t } = useTranslation('settings')
  if (options.length === 0) return null

  // When no explicit default is set, the first option is the implicit default
  const effectiveValue = value ?? options[0]?.connectionId ?? ''

  return (
    <div className="border-b border-white/5 pb-3 mb-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-pearl/70 font-medium">{t('email.defaultAccount')}</span>
        {saving && <Loader2 size={12} className="text-pearl/55 animate-spin" />}
      </div>
      <select
        value={effectiveValue}
        disabled={saving}
        onChange={(e) => { void onChange(e.target.value) }}
        className="w-full text-sm bg-midnight/40 border border-white/10 rounded-lg px-3 py-2 text-ivory focus:outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-60"
      >
        {options.map(opt => (
          <option key={opt.connectionId} value={opt.connectionId}>
            {opt.displayName} ({providerOptionLabel(opt.provider)})
          </option>
        ))}
      </select>
      <p className="text-xs text-pearl/50 mt-1.5 leading-relaxed">{t('email.defaultAccountHint')}</p>
      {error && <p className="text-xs text-rose mt-1">{t('email.saveFailed')}</p>}
    </div>
  )
}

function ProviderIcon({ provider }: { provider: string }) {
  if (provider === 'microsoft') {
    return <div className="w-6 h-6 rounded bg-[#0078D4]/20 flex items-center justify-center shrink-0"><Mail size={12} className="text-[#4aa3e5]" /></div>
  }
  return <div className="w-6 h-6 rounded bg-[#4285F4]/20 flex items-center justify-center shrink-0"><Mail size={12} className="text-[#6ea3ff]" /></div>
}

/** Short label shown after the address in the default picker dropdown. */
function providerOptionLabel(provider: string): string {
  if (provider === 'tendbot') return 'TendBot'
  if (provider === 'microsoft') return 'Outlook'
  return 'Gmail'
}

function providerLabel(provider: string, t: (k: string) => string): string {
  if (provider === 'microsoft') return t('email.providerOutlook')
  if (provider === 'google') return t('email.providerGmail')
  return provider
}
